import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { appRoutes } from './app.routes';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HttpClient, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { authInterceptor } from '../services/interceptors/auth.interceptor';
import { errorRefInterceptor } from '../services/interceptors/error-ref.interceptor';
import { directoryDemoBackendInterceptor } from '../services/demo-backend/directory-demo-backend.interceptor';
import { commsDemoBackendInterceptor } from '../services/demo-backend/comms-demo-backend.interceptor';
import { homeDemoBackendInterceptor } from '../services/demo-backend/home-demo-backend.interceptor';
import { profileDemoBackendInterceptor } from '../services/demo-backend/profile-demo-backend.interceptor';
import { billingDemoBackendInterceptor } from '../services/demo-backend/billing-demo-backend.interceptor';
import { financesDemoBackendInterceptor } from '../services/demo-backend/finances-demo-backend.interceptor';
import { priceSummaryDemoBackendInterceptor } from '../services/demo-backend/price-summary-demo-backend.interceptor';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { MatDialogModule } from '@angular/material/dialog';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatNativeDateModule } from '@angular/material/core';
import { provideIcons } from './vex/vex-core/icons/icons.provider';
import { provideLuxon } from './vex/vex-core/luxon/luxon.provider';
import { provideVex } from '@vex/vex.provider';
import { provideNavigation } from './vex/vex-core/navigation/navigation.provider';
import { provideQuillConfig } from 'ngx-quill';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha';
import { environment } from '../environments/environment';
import { ToastrModule } from 'ngx-toastr';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { InitService } from './init.service';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function initializeApp(initService: InitService): () => Promise<void> {
  return () => initService.loadHeaderData().then(() => void 0);
}

export const appConfig: ApplicationConfig = {
  providers: [
    // Angular 21 arranca en modo ZONELESS por defecto. La app está escrita asumiendo
    // detección de cambios por zona (muta campos tras await/subscribe sin signals ni
    // markForCheck), así que restauramos explícitamente el CD basado en Zone.js
    // (zone.js sigue importado en polyfills.ts). Sin esto, las vistas no repintan tras
    // cargas asíncronas hasta que ocurre un evento que Angular conoce.
    provideZoneChangeDetection(),
    importProvidersFrom(
      BrowserModule,
      MatDialogModule,
      MatBottomSheetModule,
      MatNativeDateModule,
      RecaptchaV3Module,
      ToastrModule.forRoot(
        {
          timeOut: 4000,
          positionClass: 'toast-center-center',
          preventDuplicates: true
        }
      ),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient]
        }
      }),
    ),
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.recaptchaSiteKey },
    provideRouter(
      appRoutes,
      // TODO: Add preloading withPreloading(),
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled'
      })
    ),
    provideAnimations(),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          // App maneja su propio theming (VEX, 7 temas). Forzamos PrimeNG en modo claro
          // (equivalente al antiguo tema 'tailwind-light') usando una clase inexistente.
          darkModeSelector: '.app-dark-mode'
        }
      }
    }),
    provideHttpClient(
      // directoryDemoBackendInterceptor stands in for the directory endpoints the API does
      // not have yet. It substitutes nothing until a request 404s and fills no field the
      // server already sends, so it cancels itself out endpoint by endpoint as the back end
      // lands. Delete the word below and the demo-backend folder to remove it entirely.
      // homeDemoBackendInterceptor does the same for the new Home, on /home only, and labels
      // everything it substitutes (see home-demo-backend.ts). billingDemoBackendInterceptor does it for
      // Settings › Plans & billing, on that page only (see billing-demo-backend.ts), and
      // financesDemoBackendInterceptor for Finances, on /finances only (see finances-demo-backend.ts).
      // priceSummaryDemoBackendInterceptor does it for the create forms' price summary, on /create/… only
      // and only while that summary is switched on (see price-summary-demo-backend.ts).
      withInterceptors([authInterceptor, errorRefInterceptor, directoryDemoBackendInterceptor, commsDemoBackendInterceptor, homeDemoBackendInterceptor, profileDemoBackendInterceptor, billingDemoBackendInterceptor, financesDemoBackendInterceptor, priceSummaryDemoBackendInterceptor]),
      withInterceptorsFromDi()
    ),

    provideVex({
      /**
       * Only themes that are available in the config in tailwind.config.ts should be listed here.
       * Any theme not listed here will not be available in the config panel.
       */
      availableThemes: [
        {
          name: 'Cielo',
          className: 'vex-theme-default'
        },
        {
          name: 'Malva',
          className: 'vex-theme-malva'
        },
        {
          name: 'Coral',
          className: 'vex-theme-coral'
        },
        {
          name: 'Indigo',
          className: 'vex-theme-indigo'
        },
        {
          name: 'Lime',
          className: 'vex-theme-lime'
        }
      ],
      availableAccents: [
        {
          name: 'Porcelana',
          className: 'vex-accent-porcelana'
        },
        {
          name: 'Indigo',
          className: 'vex-accent-indigo'
        },
        {
          name: 'Lime',
          className: 'vex-accent-lime'
        },
        {
          name: 'Amber',
          className: 'vex-accent-amber'
        },
        {
          name: 'Sky',
          className: 'vex-accent-sky'
        }
      ]
    }),
    provideNavigation(),
    provideIcons(),
    provideLuxon(),
    provideQuillConfig({
      modules: {
        toolbar: [
          ['bold', 'italic', 'underline', 'strike'],
          ['blockquote', 'code-block'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ['clean'],
          ['link', 'image']
        ]
      }
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [InitService],
      multi: true
    }
  ]
};
